/**
 * Vista alternativa de Casas en formato tabla tradicional
 * (Casa / Titular / Último pago / Estado / $Deuda AA / $Abono / $Saldo a hoy).
 */
import type { CasaAnualResumen } from '../../lib/api/casas-tesoreria';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from './Monto';

// Sprint 17 S17-Z5: ver GrillaCasasAnual para detalle de los 3 estados
// de "al día" (real verde, con saldo amarillo) + "atrasada" rojo.
const ESTILO_ESTADO = {
  al_dia: { bg: 'rgba(95,217,177,0.14)', text: '#5fd9b1', label: 'Al día' },
  al_dia_pendiente_semestre: {
    bg: 'rgba(255,221,87,0.16)',
    text: '#ffdb5a',
    label: 'Al día (con saldo)',
  },
  atrasada: {
    bg: 'rgba(255,122,122,0.14)',
    text: '#ff7a7a',
    label: 'Atrasada',
  },
  suspendida: {
    bg: 'rgba(255,183,117,0.14)',
    text: '#ffb775',
    label: 'Suspendida',
  },
} as const;

export interface TablaCasasProps {
  casas: CasaAnualResumen[];
  onCasaClick: (casa: CasaAnualResumen) => void;
}

export function TablaCasas({ casas, onCasaClick }: TablaCasasProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
            <th className="text-left font-medium py-2">Casa</th>
            <th className="text-left font-medium py-2">Titular</th>
            <th className="text-left font-medium py-2">Último pago</th>
            <th className="text-center font-medium py-2">Estado</th>
            <th
              className="text-right font-medium py-2"
              title="Deuda de años anteriores al 1-ene (apertura)"
            >
              $Deuda AA
            </th>
            <th
              className="text-right font-medium py-2"
              title="Abonado este año a la deuda de años anteriores"
            >
              $Abono
            </th>
            <th
              className="text-right font-medium py-2"
              title="Saldo total exigible a hoy, neto de saldo a favor"
            >
              $Saldo a hoy
            </th>
          </tr>
        </thead>
        <tbody>
          {casas.map((c) => {
            const e = ESTILO_ESTADO[c.estado];
            return (
              <tr
                key={c.lote_id}
                onClick={() => onCasaClick(c)}
                className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer"
              >
                <td className="py-2 text-text-primary font-medium">
                  <Num>{c.identificador}</Num>
                </td>
                <td className="py-2 text-text-secondary">
                  {c.titular_nombre || '—'}
                </td>
                <td className="py-2 text-text-secondary text-xs">
                  {c.ultimo_pago_fecha
                    ? formatFecha(c.ultimo_pago_fecha)
                    : 'Sin pagos'}
                </td>
                <td className="py-2 text-center">
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: e.bg, color: e.text }}
                  >
                    {e.label}
                  </span>
                </td>
                {/* $ Deuda AA — apertura (histórico, neutro) */}
                <td className="py-2 text-right text-text-secondary text-xs">
                  {c.deuda_anio_anterior > 0 ? (
                    <Monto value={c.deuda_anio_anterior} />
                  ) : (
                    '—'
                  )}
                </td>
                {/* $ Abono — abonado este año a la deuda AA (verde) */}
                <td
                  className={`py-2 text-right text-xs ${
                    c.abono_anio_anterior > 0
                      ? 'text-[#5fd9b1] font-medium'
                      : 'text-text-muted'
                  }`}
                >
                  {c.abono_anio_anterior > 0 ? (
                    <Monto value={c.abono_anio_anterior} />
                  ) : (
                    '—'
                  )}
                </td>
                {/* $ Saldo a hoy — neto MTD: >0 debe (rojo), <0 favor (verde) */}
                <td
                  className={`py-2 text-right font-medium ${
                    c.saldo_a_hoy > 0
                      ? 'text-red-400'
                      : c.saldo_a_hoy < 0
                        ? 'text-[#5fd9b1]'
                        : 'text-text-secondary'
                  }`}
                  title={c.saldo_a_hoy < 0 ? 'Saldo a favor disponible' : undefined}
                >
                  {c.saldo_a_hoy > 0 ? (
                    <Monto value={c.saldo_a_hoy} />
                  ) : c.saldo_a_hoy < 0 ? (
                    <>
                      −<Monto value={-c.saldo_a_hoy} />
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
          {casas.length === 0 && (
            <tr>
              <td
                colSpan={7}
                className="text-center text-text-muted py-8 text-sm"
              >
                Sin casas para mostrar con los filtros actuales.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
